import {
  useScheduleEditorGetItemByIdQuery,
  useScheduleEditorUpdateItemMutation,
  useGetTagsByResourceQuery,
  useAddTagsToResourceMutation,
  useRemoveTagsFromResourceMutation,
} from '@graphql/generated';
import { useCallback, useMemo, useRef } from 'react';
import type { ScheduleEditorData, ScheduleTag } from '../ScheduleEditor.types';
import { ScheduleFormDisplay, ScheduleFormDisplaySkeleton } from './form/ScheduleFormDisplay';
import React from 'react';
import { EMPTY_FORM_DATA, ScheduleEditorProps } from '../ScheduleEditor';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useDeleteScheduleItem } from '../hooks/useDeleteScheduleItem';
import { formatDateAsISO8601TZ } from './form/hooks/useGetEventTimezone';
import { addError } from '@shared/utils/logger';

const RESOURCE_TYPE = 'SCHEDULE_ITEM';

export const EditScheduleItem = (props: ScheduleEditorProps & { mode: 'edit' }) => {
  const { scheduleItemId, eventId, onSuccess, onError, onCancel, refetch } = props;
  const { deleteScheduleItem } = useDeleteScheduleItem({
    refetch,
  });
  const { data: scheduleQueryResults, loading } = useScheduleEditorGetItemByIdQuery({
    variables: {
      scheduleId: scheduleItemId,
    },
    batchMode: 'fast',
  });

  // Fetch initial tags for the resource
  const { data: tagsData, loading: tagsLoading } = useGetTagsByResourceQuery({
    variables: { eventId, resourceId: scheduleItemId },
    batchMode: 'fast',
  });

  // Store initial tags for diff calculation on submit
  const initialTagsRef = useRef<ReadonlyArray<ScheduleTag>>([]);

  const [updateItem, { loading: isSaving }] = useScheduleEditorUpdateItemMutation();
  const [addTagsToResource] = useAddTagsToResourceMutation();
  const [removeTagsFromResource] = useRemoveTagsFromResourceMutation();

  const handleSubmit = useCallback(
    async (data: ScheduleEditorData) => {
      try {
        // Update schedule item
        const results = await updateItem({
          variables: {
            payload: {
              scheduleId: scheduleItemId,
              startTimeISO8601TZ: formatDateAsISO8601TZ(data.startTime, data.timezone),
              endTimeISO8601TZ: formatDateAsISO8601TZ(data.endTime, data.timezone),
              description: data.description,
              name: data.name,
              virtualEventLink: data.virtualEventUrl,
              dressCode: data.dressCode,
            },
          },
          refetchQueries: ['ScheduleEditorGetItemById', ...(refetch ?? [])],
          awaitRefetchQueries: true,
        });

        // Handle tag changes
        const initialTagIds = new Set(initialTagsRef.current.map(t => t.id));
        const finalTagIds = new Set(data.tags.map(t => t.id));

        const addedTagIds = data.tags.filter(t => !initialTagIds.has(t.id)).map(t => t.id);
        const removedTagIds = initialTagsRef.current.filter(t => !finalTagIds.has(t.id)).map(t => t.id);

        // Call mutations for added tags
        if (addedTagIds.length > 0) {
          await addTagsToResource({
            variables: {
              eventId,
              tagIds: addedTagIds,
              resourceId: scheduleItemId,
              resourceType: RESOURCE_TYPE,
            },
            refetchQueries: ['GetTagsByResource'],
          });
        }

        // Call mutations for removed tags
        if (removedTagIds.length > 0) {
          await removeTagsFromResource({
            variables: {
              eventId,
              tagIds: removedTagIds,
              resourceId: scheduleItemId,
            },
            refetchQueries: ['GetTagsByResource'],
          });
        }

        // Update initial tags ref after successful save
        initialTagsRef.current = data.tags;

        if (results.data?.updateEventScheduleItem) {
          onSuccess(results.data.updateEventScheduleItem);
        } else {
          onError?.(results.errors);
        }
      } catch (error) {
        addError(error, { context: 'EditScheduleItem', message: 'Failed to save schedule item' });
        onError?.(error);
      }
    },
    [updateItem, addTagsToResource, removeTagsFromResource, eventId, onError, onSuccess, refetch, scheduleItemId],
  );

  const initialData = useMemo(() => {
    const scheduleData = scheduleQueryResults?.getAdminEventScheduleItemById;

    if (!scheduleData) {
      return EMPTY_FORM_DATA;
    }

    // Get initial tags from the tags query
    const initialTags = tagsData?.getTagsByResource ?? [];

    // Store initial tags for diff calculation
    initialTagsRef.current = initialTags;

    const data: ScheduleEditorData = {
      name: scheduleData.name,
      description: scheduleData.description ?? '',
      startTime: scheduleData.startTime ? new Date(scheduleData.startTime?.dateString) : null,
      endTime: scheduleData.endTime ? new Date(scheduleData.endTime?.dateString) : null,
      timezone: scheduleData.timezone?.replace(' ', '_') ?? '',
      virtualEventUrl: scheduleData.virtualEventLink?.urlLinkText ?? null,
      dressCode: scheduleData.dressCode ?? '',
      tags: initialTags,
    };

    return data;
  }, [scheduleQueryResults, tagsData]);

  return (
    <SkeletonGroup
      placeholder={<ScheduleFormDisplaySkeleton />}
      isReady={!loading && !tagsLoading && !!scheduleQueryResults}
    >
      <ScheduleFormDisplay
        isSaving={isSaving}
        data={initialData}
        onSubmit={handleSubmit}
        onCancel={() => {
          onCancel?.();
        }}
        onDelete={() => {
          deleteScheduleItem(scheduleItemId);
          onCancel?.();
        }}
        eventId={eventId}
      />
    </SkeletonGroup>
  );
};
